// Persona scorer — stamps a user with one or more persona tags so the
// Welcome Kit and Drip can BIAS card / question order toward what the
// user will care about most. Bias, not gate: every user still sees
// every card and question; only the order changes.
//
// Pure function — same input contract as capability-map.js. The API
// route (app/api/persona/route.js) does the DB reads and feeds in
// the four input maps.
//
// Tags are non-exclusive — a busy musician mom gets {family, foodie,
// musician, creative} and the Welcome Kit / Drip ordering blends them.

function parseArray(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
}

// Each persona is a Set-membership rule. Returning true tags the user.
// Inputs:
//   - profile        : profiles row (name, work_context fallback, drip_answers)
//   - preferences    : flat map of pref key → value (parsed if JSON)
//   - integrations   : Set of connected provider strings
//   - dripAnswers    : map of drip question id → answer
const PERSONAS = {
  creative: ({ preferences }) => {
    const rec = parseArray(preferences?.recreation);
    return rec.some((r) => ["Music", "Art", "Crafts", "Photography", "Writing", "Make stuff"].includes(r));
  },

  developer: ({ preferences, integrations, dripAnswers }) => {
    if (integrations.has("github")) return true;
    const drives = parseArray(preferences?.drives);
    if (drives.includes("Building something")) return true;
    const apps = parseArray(dripAnswers?.apps_you_use);
    if (apps.includes("GitHub") || apps.includes("Linear")) return true;
    return false;
  },

  business: ({ preferences, integrations }) => {
    if (integrations.has("quickbooks") || integrations.has("stripe") ||
        integrations.has("square") || integrations.has("shopify") ||
        integrations.has("toast") || integrations.has("vagaro")) return true;
    const work = preferences?.work_context;
    if (typeof work === "string" && /business|owner|self-employ/i.test(work)) return true;
    return false;
  },

  family: ({ preferences }) => {
    const household = parseArray(preferences?.household);
    return household.some((h) => ["Partner", "Kids", "Spouse", "Family"].includes(h));
  },

  health: ({ preferences, integrations }) => {
    if (integrations.has("fitbit") || integrations.has("strava")) return true;
    const rec = parseArray(preferences?.recreation);
    if (rec.some((r) => ["Fitness", "Outdoors", "Running", "Yoga"].includes(r))) return true;
    const drives = parseArray(preferences?.drives);
    if (drives.includes("Health")) return true;
    return false;
  },

  foodie: ({ preferences, dripAnswers }) => {
    const rec = parseArray(preferences?.recreation);
    if (rec.some((r) => ["Cook", "Cooking", "Baking"].includes(r))) return true;
    // capture_habit doesn't tell us they cook, but if their what_to_delegate
    // mentions meals/recipes/groceries, count it.
    const delegate = dripAnswers?.what_to_delegate;
    if (typeof delegate === "string" && /meal|dinner|recipe|grocer/i.test(delegate)) return true;
    return false;
  },

  musician: ({ preferences, integrations }) => {
    const rec = parseArray(preferences?.recreation);
    const likesMusic = rec.includes("Music");
    if (!likesMusic) return false;
    // Music in recreation alone isn't enough — many people listen but don't make.
    // Tag musician when there's an active music surface (Spotify/Apple Music
    // connected, or Fabric in use signaled via integrations.has("fabric_active")).
    return integrations.has("spotify") || integrations.has("apple_music") || integrations.has("fabric_active");
  },

  learner: ({ preferences, integrations }) => {
    if (integrations.has("readwise")) return true;
    const rec = parseArray(preferences?.recreation);
    if (rec.some((r) => ["Reading", "Learning", "Study"].includes(r))) return true;
    return false;
  },
};

export const PERSONA_IDS = Object.keys(PERSONAS);

/**
 * Compute the persona tags for a user from their captured signals.
 * @param profile        — profiles row
 * @param preferences    — flat map of pref key → value
 * @param integrations   — Set<string> of connected provider ids
 * @param dripAnswers    — map of drip question id → answer
 * @returns Set<string>  — persona tags (may be empty)
 */
export function getUserPersonas(profile, preferences = {}, integrations = new Set(), dripAnswers = {}) {
  const ctx = {
    profile: profile || {},
    preferences,
    integrations: integrations instanceof Set ? integrations : new Set(integrations || []),
    dripAnswers,
  };
  const tags = new Set();
  for (const [id, rule] of Object.entries(PERSONAS)) {
    try { if (rule(ctx)) tags.add(id); } catch { /* defensive */ }
  }
  return tags;
}

/**
 * Stable persona-biased sort. Items whose `personas` array intersects the
 * user's persona Set bubble first; remaining items keep their original
 * order. Items without a `personas` field are treated as universal and
 * never bubble (their position is preserved relative to other universals).
 *
 * @param items     — array of objects with optional `personas: string[]`
 * @param userTags  — Set<string> of the user's persona tags
 * @returns sorted copy of items
 */
export function sortByPersona(items, userTags) {
  if (!userTags || userTags.size === 0) return [...items];
  return [...items]
    .map((item, idx) => ({ item, idx, match: matchScore(item, userTags) }))
    .sort((a, b) => {
      if (b.match !== a.match) return b.match - a.match;
      return a.idx - b.idx;
    })
    .map(({ item }) => item);
}

function matchScore(item, userTags) {
  const personas = Array.isArray(item?.personas) ? item.personas : [];
  if (personas.length === 0) return 0;
  let n = 0;
  for (const p of personas) if (userTags.has(p)) n += 1;
  return n;
}
